import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Lightbulb, ThumbsUp } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORY_META = {
  'cross-sell':    { color: 'bg-purple-100 text-purple-800', label: 'Cross-sell' },
  'renewals':      { color: 'bg-emerald-100 text-emerald-800', label: 'Renewals' },
  'referrals':     { color: 'bg-blue-100 text-blue-800', label: 'Referrals' },
  'digital':       { color: 'bg-pink-100 text-pink-800', label: 'Digital' },
  'networking':    { color: 'bg-amber-100 text-amber-800', label: 'Networking' },
  'response-time': { color: 'bg-red-100 text-red-800', label: 'Response time' },
  'claims':        { color: 'bg-indigo-100 text-indigo-800', label: 'Claims' },
  'pricing':       { color: 'bg-orange-100 text-orange-800', label: 'Pricing' },
  'growth':        { color: 'bg-slate-100 text-slate-800', label: 'Growth' },
};

/**
 * Tip of the Day — founder-authored coaching content, one per UTC day,
 * rotated deterministically so every agent sees the same tip on the same day.
 *
 * Sits in the Dashboard right sidebar between the Business Health card and
 * the Target/Streak stats. Compact enough to skim in 15 seconds, always
 * visible enough to become a habit.
 */
const TipOfTheDayCard = () => {
  const [state, setState] = useState(null);
  const [loading, setLoading] = useState(true);
  const [reacting, setReacting] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/tips/today`, { withCredentials: true });
        setState(data);
      } catch (_) { /* silent */ }
      finally { setLoading(false); }
    })();
  }, []);

  const toggleHelpful = async () => {
    if (!state?.tip?.tip_id) return;
    setReacting(true);
    try {
      const { data } = await axios.post(
        `${API}/tips/react`,
        { tip_id: state.tip.tip_id, reaction: 'helpful' },
        { withCredentials: true },
      );
      setState((s) => ({ ...s, tip: { ...s.tip, you_marked_helpful: data.you_marked_helpful, helpful_count: data.helpful_count } }));
      if (data.you_marked_helpful) toast.success('Thanks — that helps the founder pick more like this.');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not save reaction');
    } finally { setReacting(false); }
  };

  if (loading) {
    return (
      <div className="rounded-xl border border-slate-200 bg-white p-4 animate-pulse" data-testid="tip-loading">
        <div className="h-3 w-24 bg-slate-200 rounded mb-3" />
        <div className="h-4 bg-slate-200 rounded mb-2" />
        <div className="h-3 bg-slate-200 rounded w-10/12" />
      </div>
    );
  }

  if (!state?.tip) {
    // Content bank empty — quietly hide (admin sees seed message in their manager).
    return null;
  }

  const tip = state.tip;
  const cat = CATEGORY_META[tip.category] || CATEGORY_META.growth;

  return (
    <div
      className="rounded-xl border border-amber-200 bg-gradient-to-br from-amber-50 to-white p-4"
      data-testid="tip-of-the-day-card"
    >
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <Lightbulb className="h-4 w-4 text-amber-600" />
          <p className="text-sm font-semibold text-slate-900" style={{ fontFamily: 'Manrope' }}>
            Tip of the Day
          </p>
        </div>
        <span
          className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${cat.color}`}
          data-testid="tip-category-badge"
        >
          {cat.label}
        </span>
      </div>

      <h3 className="text-[15px] font-semibold text-slate-900 leading-snug mb-1.5" data-testid="tip-title">
        {tip.title}
      </h3>
      <p className="text-[13px] text-slate-700 leading-snug whitespace-pre-wrap" data-testid="tip-body">
        {tip.body}
      </p>

      <div className="flex items-center justify-between mt-3 pt-2 border-t border-amber-100">
        <button
          type="button"
          onClick={toggleHelpful}
          disabled={reacting}
          data-testid="tip-helpful-button"
          className={`flex items-center gap-1.5 text-[11px] font-medium px-2.5 py-1 rounded-full border transition-colors disabled:opacity-50 ${
            tip.you_marked_helpful
              ? 'bg-emerald-100 text-emerald-800 border-emerald-300'
              : 'bg-white text-slate-600 border-slate-300 hover:bg-slate-50'
          }`}
          title={tip.you_marked_helpful ? "Tap to remove your reaction" : "Mark this tip as helpful"}
        >
          <ThumbsUp className={`h-3 w-3 ${tip.you_marked_helpful ? 'fill-emerald-600 text-emerald-600' : ''}`} />
          <span data-testid="tip-helpful-label">{tip.you_marked_helpful ? 'Marked helpful' : 'Helpful?'}</span>
          {tip.helpful_count > 0 && (
            <span className="text-slate-500" data-testid="tip-helpful-count">· {tip.helpful_count}</span>
          )}
        </button>
        <span className="text-[10px] text-slate-400" data-testid="tip-day-index">
          Day {tip.day_index + 1} of {tip.bank_size}
        </span>
      </div>
    </div>
  );
};

export default TipOfTheDayCard;
